import React, { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import TradingModeShell from "@/components/TradingModeShell";
import AuthPage from "@/pages/AuthPage";
import CalendarView from "@/pages/CalendarView";
import ComparisonView from "@/pages/ComparisonView";
import Dashboard from "@/pages/Dashboard";
import ImportView from "@/pages/ImportView";
import ModeSelectionPage from "@/pages/ModeSelectionPage";
import PlaybookView from "@/pages/PlaybookView";
import PortfolioView from "@/pages/PortfolioView";
import Settings from "@/pages/Settings";
import Statistics from "@/pages/Statistics";
import TradesList from "@/pages/TradesList";
import OnboardingFlow from "@/components/OnboardingFlow";
import {
  clearLiveModeConfirmed,
  clearSessionMode,
  getInitialMode,
  getStrategyStorageKey,
  isValidTradingMode,
  persistModeSelection,
  syncModeOnNavigation,
  TRADING_MODES,
} from "@/lib/tradingMode";

const APP_PAGES = [
  { suffix: "", Component: Dashboard },
  { suffix: "statistics", Component: Statistics },
  { suffix: "trades", Component: TradesList },
  { suffix: "calendar", Component: CalendarView },
  { suffix: "comparison", Component: ComparisonView },
  { suffix: "import", Component: ImportView },
  { suffix: "playbook", Component: PlaybookView },
  { suffix: "settings", Component: Settings },
  { suffix: "portfolio", Component: PortfolioView },
];

function RootRedirect({ user, activeMode }) {
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (activeMode) {
    return <Navigate to={`/${activeMode}`} replace />;
  }

  return <Navigate to="/mode-select" replace />;
}

function ModeSelectionRoute({ user, activeMode, onSelectMode }) {
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (activeMode) {
    return <Navigate to={`/${activeMode}`} replace />;
  }

  return <ModeSelectionPage onSelectMode={onSelectMode} />;
}

function LegacyRouteRedirect({ user, activeMode, suffix = "" }) {
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!activeMode) {
    return <Navigate to="/mode-select" replace />;
  }

  const basePath = `/${activeMode}`;
  const targetPath = suffix ? `${basePath}/${suffix}` : basePath;
  return <Navigate to={targetPath} replace />;
}

function App() {
  const [user, setUser] = useState(null);
  const [activeMode, setActiveMode] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("user");

    if (token && userData) {
      setUser(JSON.parse(userData));
      setActiveMode(getInitialMode());
    }

    setLoading(false);
  }, []);

  const handleLogin = useCallback((userData, token, refreshToken) => {
    localStorage.setItem("token", token);
    localStorage.setItem("access_token", token);
    if (refreshToken) {
      localStorage.setItem("refresh_token", refreshToken);
    }
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
    setActiveMode(getInitialMode());
  }, []);

  const handleModeSelection = useCallback((mode, rememberChoice) => {
    if (!isValidTradingMode(mode)) {
      return;
    }
    const selectedMode = persistModeSelection(mode, rememberChoice);
    setActiveMode(selectedMode);
  }, []);

  const handleModeNavigation = useCallback((mode) => {
    if (!isValidTradingMode(mode)) {
      return;
    }
    syncModeOnNavigation(mode);
    setActiveMode(mode);
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    localStorage.removeItem("currentStrategy");
    localStorage.removeItem(getStrategyStorageKey(TRADING_MODES.BACKTESTING));
    localStorage.removeItem(getStrategyStorageKey(TRADING_MODES.LIVE));
    clearSessionMode();
    clearLiveModeConfirmed();
    setUser(null);
    setActiveMode(getInitialMode());
  }, []);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <ErrorBoundary>
    <>
      <BrowserRouter>
        <Routes>
          <Route
            path="/auth"
            element={
              user ? (
                <Navigate to={activeMode ? `/${activeMode}` : "/mode-select"} replace />
              ) : (
                <AuthPage onLogin={handleLogin} />
              )
            }
          />

          <Route
            path="/mode-select"
            element={
              <ModeSelectionRoute
                user={user}
                activeMode={activeMode}
                onSelectMode={handleModeSelection}
              />
            }
          />

          {Object.values(TRADING_MODES).flatMap((mode) =>
            APP_PAGES.map(({ suffix, Component }) => {
              const pagePath = suffix ? `/${mode}/${suffix}` : `/${mode}`;
              return (
                <Route
                  key={pagePath}
                  path={pagePath}
                  element={
                    user ? (
                      <TradingModeShell
                        user={user}
                        mode={mode}
                        activeMode={activeMode}
                        onModeChange={handleModeNavigation}
                        onLogout={handleLogout}
                      >
                        <Component />
                      </TradingModeShell>
                    ) : (
                      <Navigate to="/auth" replace />
                    )
                  }
                />
              );
            })
          )}

          <Route path="/" element={<RootRedirect user={user} activeMode={activeMode} />} />

          {APP_PAGES.filter((page) => page.suffix).map((page) => (
            <Route
              key={`legacy-${page.suffix}`}
              path={`/${page.suffix}`}
              element={<LegacyRouteRedirect user={user} activeMode={activeMode} suffix={page.suffix} />}
            />
          ))}

          <Route path="*" element={<RootRedirect user={user} activeMode={activeMode} />} />
        </Routes>
        {user && (
          <OnboardingFlow
            mode={activeMode || "backtesting"}
            user={user}
          />
        )}
      </BrowserRouter>
      <Toaster position="top-right" expand={false} richColors />
      <Analytics />
    </>
    </ErrorBoundary>
  );
}

export default App;
